import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
	type CheckUserResult,
	checkUser,
	computeAccessState,
	getRedirectTargetForAccessState,
	persistAuthResult,
	registerUser,
	updateUser,
} from "@/entities/user"
import {
	emailSendCodeApiV1AuthEmailSendCodePost,
	type UserUpdate,
} from "@/shared/api/generated-backend"
import { calculateAge } from "@/shared/lib/date"
import { getDeviceTimezone } from "@/shared/lib/timezone"
import type { RegistrationFormValues } from "../../form-validation/model/types"

async function sendEmailCode(email: string) {
	return emailSendCodeApiV1AuthEmailSendCodePost({
		body: { email },
	})
}

/**
 * The social fields are the provider link: the backend re-verifies the token and
 * ties the new account to it. Without them a provider signup creates an account
 * that the next provider login will not recognise.
 */
type RegistrationPayload = Omit<RegistrationFormValues, "dateOfBirth"> & {
	dateOfBirth?: string
	googleIdToken?: string
	appleIdentityToken?: string
	appleNonce?: string
}

type RegistrationResult = CheckUserResult & {
	redirectTo?: string | null
}

interface UseSubmitRegistrationOptions {
	onSuccess: (result: RegistrationResult) => void
}

export function useSubmitRegistration({
	onSuccess,
}: UseSubmitRegistrationOptions) {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (values: RegistrationPayload) => {
			// Telegram: проверяем существующего пользователя и обновляем данные
			const tgUserId = values.tgUserId
			if (tgUserId) {
				const checkResult = await checkUser({ tg_user_id: tgUserId })

				if (checkResult.exists) {
					const update: UserUpdate = {
						id: checkResult.user.id,
						luni_name: values.firstName,
						email: values.email,
					}
					const timezone = getDeviceTimezone()
					if (timezone !== undefined) update.timezone = timezone
					if (!checkResult.user.date_of_birth) {
						update.date_of_birth = values.dateOfBirth || null
						update.age = null
						if (values.dateOfBirth) {
							update.age = calculateAge(values.dateOfBirth)
						}
					}
					const updatedUser = await updateUser(update)
					const accessState = computeAccessState(updatedUser)
					const needsEmailVerification =
						accessState.kind === "blocked" &&
						accessState.reason === "email_not_verified"
					if (needsEmailVerification) {
						await sendEmailCode(values.email)
					}
					let redirectTo: string | null = null
					if (!needsEmailVerification) {
						redirectTo = getRedirectTargetForAccessState(accessState)
					}
					return {
						...checkResult,
						user: updatedUser,
						redirectTo,
					}
				}
			}

			// New user — register
			let age: number | null = null
			if (values.dateOfBirth) age = calculateAge(values.dateOfBirth)
			return registerUser({
				tg_user_id: values.tgUserId || null,
				tg_username: values.tgUsername || null,
				luni_name: values.firstName,
				email: values.email,
				gender: values.gender,
				city: values.city || null,
				date_of_birth: values.dateOfBirth || null,
				age,
				tg_ref_code: values.tgRefCode || null,
				app_language: values.appLanguage,
				is_invisible: false,
				blocked: null,
				google_id_token: values.googleIdToken ?? null,
				apple_identity_token: values.appleIdentityToken ?? null,
				apple_nonce: values.appleNonce ?? null,
			})
		},
		onSuccess: async (result) => {
			await persistAuthResult(queryClient, result)
			onSuccess(result)
		},
	})
}
